import { EmailVerifyForm } from '@/components/EmailVerifyForm';
import { AccountFound } from '@/components/Layout/LeftPane/AccountFound';
import IDontKnowEmail from '@/pages/i_dont_know_email.page';
import NoAccountFoundPage from '@/pages/no_account_found.page';
import TicketsFoundPage from '@/pages/order-details/tickets-found.page';
import { Attendee, Order } from '@/types';
import { useRouter } from 'next/router';
import { PropsWithChildren, useState } from 'react';
import styles from './LeftPane.module.scss';
import { trackEvent } from './LeftPaneHeap';
import { TicketFound } from './TicketFound';
import { TopNav } from './TopNav';

interface LeftPaneProps {
  pageType: string;
  emailId?: string;
}

export type PageType =
  | 'emailVerify'
  | 'iDontKnowEmail'
  | 'accountFound'
  | 'onLookUpAnotherWay'
  | 'noAccountFound'
  | 'ticketsFound'
  | 'ticketDetails';

export const LeftPane: React.FC<PropsWithChildren<LeftPaneProps>> = ({
  children,
  pageType,
  emailId,
}) => {
  const [currentPage, setCurrentPage] = useState<PageType | string>(pageType);
  const [foundOrders, setFoundOrders] = useState<Order[]>([]);
  const [order, setSelectedOrder] = useState<Order>();
  const [attendeesList, setAttendeesList] = useState<Attendee[]>([]);
  const [previousPage, setPreviousPage] = useState<PageType | string>('emailVerify');
  const [userEmail, setUserEmail] = useState<string>(emailId || '');
  const router = useRouter();

  const handleTicketsFound = (orders: Order[]) => {
    sessionStorage.clear();
    setFoundOrders(orders);
    setPreviousPage(currentPage);
    setCurrentPage('ticketsFound');
  };

  const handleOrderClick = (order: Order, attendeesList: Attendee[]) => {
    setSelectedOrder(order);
    setAttendeesList(attendeesList);
    setPreviousPage(currentPage);
    setCurrentPage('ticketDetails');
    trackEvent('OrderCardSelected', 'OrderCardSelected');
  };

  const handleAccountFound = (email: string) => {
    setUserEmail(email);
    setPreviousPage(currentPage);
    setCurrentPage('accountFound');
  };

  const handleBackNavigation = () => {
    const navigationMap: Record<typeof currentPage, string> = {
      ticketsFound: 'iDontKnowEmail',
      ticketDetails: 'ticketsFound',
      accountFound: 'emailVerify',
    };
    setCurrentPage(navigationMap[currentPage] || 'emailVerify');
  };

  const getNavUrl = () => {
    const navUrlMap: Record<typeof currentPage, string> = {
      emailVerify: 'help-center',
      iDontKnowEmail: '/',
      accountFound: 'back',
      onLookUpAnotherWay: '/',
      noAccountFound: '/',
      ticketsFound: 'back',
      ticketDetails: 'back',
    };
    return navUrlMap[currentPage] || 'help-center';
  };

  const pageComponents: Record<typeof currentPage, JSX.Element> = {
    emailVerify: <EmailVerifyForm onAccountFound={handleAccountFound} />,
    iDontKnowEmail: (
      <IDontKnowEmail setCurrentPage={setCurrentPage} handleTicketsFound={handleTicketsFound} />
    ),
    accountFound: <AccountFound emailId={userEmail} />,
    onLookUpAnotherWay: (
      <IDontKnowEmail setCurrentPage={setCurrentPage} handleTicketsFound={handleTicketsFound} />
    ),
    noAccountFound: (
      <NoAccountFoundPage
        setCurrentPage={setCurrentPage}
        handleTicketsFound={handleTicketsFound}
        emailId={userEmail}
      />
    ),
    ticketsFound: (
      <TicketsFoundPage
        setCurrentPage={setCurrentPage}
        orders={foundOrders}
        handleOrderClick={handleOrderClick}
      />
    ),
    ticketDetails: (
      <TicketFound
        order={order as Order}
        attendeesList={attendeesList}
        setCurrentPage={setCurrentPage}
      />
    ),
  };

  return (
    <div
      className={`${styles.left_pane_layout} ${currentPage === 'emailVerify' ? styles.compact_layout : ''}`}
    >
      {currentPage !== 'emailVerify' && (
        <TopNav nav_url={getNavUrl()} handleBackNavigation={handleBackNavigation} />
      )}
      {pageComponents[currentPage]}
    </div>
  );
};
